import { isMeetingSessionRoleGreaterThan, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { MediaStreamExtended } from "@kalyzee/kast-web-sdk/webrtc";
import { MeetingSessionHighlightingState, MeetingSessionRole } from "@kalyzee/kast-web-sdk/websocket";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppStoreDispatch } from "../contexts/appStore/context";
import { useMeetingContainerContext } from "../contexts/meetingContainer/context";
import { canShareScreen, isScreenShareMedia, SCREEN_SHARE_MEDIA_NAME, ScreenCapture } from "../helpers/screenShare";
import { toastError } from "../helpers/toast";
import { keepMedias, useDynamicMedia } from "./meeting";

export type ScreenShareStatus = "idle" | "starting" | "ongoing";

export interface ScreenShareState {
  /** The button has its place: the runtime can capture and the session may publish. */
  available: boolean;
  status: ScreenShareStatus;
  toggle: () => Promise<void>;
}

/**
 * Publishes a screen capture as an extra media of our session, next to the
 * sources. Open to the roles above normal, and to anyone while they are on
 * the board. Losing that right mid-share, or the media disappearing on
 * the backend side, ends the share.
 *
 * Lives as long as the component holding it: call it from one that stays
 * mounted for the whole meeting, not from a menu that can close.
 */
export const useScreenShare = (): ScreenShareState => {
  const { containerAPI } = useMeetingContainerContext();
  const [session] = useMeetingSelector(containerAPI, "session");
  const sessionRef = useRefAutoUpdate(session);
  const containerAPIRef = useRefAutoUpdate(containerAPI);

  const dispatch = useAppStoreDispatch();
  const dispatchRef = useRefAutoUpdate(dispatch);

  const dynamicMedia = useDynamicMedia(containerAPI);
  const dynamicMediaRef = useRefAutoUpdate(dynamicMedia);

  const [status, setStatus] = useState<ScreenShareStatus>("idle");
  const captureRef = useRef<ScreenCapture>();
  // Set synchronously, unlike `status`: guards against a double click while
  // the picker opens.
  const startingRef = useRef(false);
  // A stop asked while starting waits for the media creation to settle:
  // removing a media not created yet would leave it behind.
  const pendingStopRef = useRef(false);
  // Whether the backend has shown the media since the share started: until it
  // does, its absence from the session is expected, not a removal.
  const mediaSeenRef = useRef(false);

  const supported = useMemo(() => canShareScreen(), []);
  const permitted =
    !!session && (isMeetingSessionRoleGreaterThan(session.role, MeetingSessionRole.NORMAL) || session.highlighting === MeetingSessionHighlightingState.ONGOING);
  const remoteMedia = session?.medias?.find(isScreenShareMedia);

  const stop = useCallback(async () => {
    if (startingRef.current) {
      pendingStopRef.current = true;
      return;
    }
    const capture = captureRef.current;
    if (!capture) return;
    captureRef.current = undefined;
    mediaSeenRef.current = false;
    capture.destroy();
    dispatchRef.current("updateSelfStream", { sourceName: SCREEN_SHARE_MEDIA_NAME, stream: undefined });
    setStatus("idle");
    await dynamicMediaRef.current.remove(SCREEN_SHARE_MEDIA_NAME);
  }, []);

  const start = useCallback(async () => {
    if (captureRef.current || startingRef.current) return;
    startingRef.current = true;
    pendingStopRef.current = false;
    setStatus("starting");

    let capture: ScreenCapture;
    try {
      capture = await ScreenCapture.start();
    } catch (err) {
      startingRef.current = false;
      // The user closing the picker lands here too: that one is no failure.
      if (!(err instanceof DOMException && err.name === "NotAllowedError")) {
        console.error("screen share - capture failed: ", err);
        toastError("Le partage d'écran a échoué.");
      }
      setStatus("idle");
      return;
    }
    captureRef.current = capture;
    capture.onEnded = () => {
      stop();
    };
    if (pendingStopRef.current) {
      startingRef.current = false;
      await stop();
      return;
    }

    const success = await dynamicMediaRef.current.set(SCREEN_SHARE_MEDIA_NAME, capture.stream, {
      extraType: "screen",
      extra: { label: "Partage d'écran", playerFit: "contain" },
      // Whatever the camera and mic are at, a share is meant to be seen.
      enabled: { audio: true, video: true },
    });
    startingRef.current = false;
    if (!success || pendingStopRef.current) {
      if (!success) toastError("Le partage d'écran a échoué.");
      await stop();
      return;
    }

    dispatchRef.current("updateSelfStream", { sourceName: SCREEN_SHARE_MEDIA_NAME, stream: new MediaStreamExtended(capture.stream) });
    setStatus("ongoing");
  }, [stop]);

  const toggle = useCallback(async () => {
    if (startingRef.current) return;
    if (captureRef.current) await stop();
    else await start();
  }, [start, stop]);

  useEffect(() => {
    if (!permitted) stop();
  }, [permitted, stop]);

  useEffect(() => {
    if (remoteMedia) {
      if (captureRef.current) {
        mediaSeenRef.current = true;
        return;
      }
      // A media left behind by a share stopped before the session listed
      // it: nothing publishes on it anymore.
      if (startingRef.current) return;
      const api = containerAPIRef.current;
      const currentSession = sessionRef.current;
      if (!api || !currentSession) return;
      api.updater.setMedias(currentSession.id, keepMedias(currentSession.medias.filter((m) => !isScreenShareMedia(m))));
      return;
    }
    if (captureRef.current && mediaSeenRef.current) stop();
  }, [remoteMedia, stop]);

  useEffect(() => {
    return () => {
      stop();
    };
  }, []);

  return {
    available: supported && permitted,
    status,
    toggle,
  };
};
